<!--
# Frontend Vue | Vendor: FairVerify | App: Phoenix | Type: ExhibitorScan
-->
<script>
import VisitorLeadScanTab from '@/components/visitor-lead/VisitorLeadScanTab.vue'
import VisitorLeadListTab from '@/components/visitor-lead/VisitorLeadListTab.vue'
import VisitorLeadSettingsTab from '@/components/visitor-lead/VisitorLeadSettingsTab.vue'
import VisitorLeadModal from '@/components/visitor-lead/VisitorLeadModal.vue'
import VisitorLeadFairSelectorModal from '@/components/visitor-lead/VisitorLeadFairSelectorModal.vue'
import VisitorLeadDebugAuthModal from '@/components/visitor-lead/VisitorLeadDebugAuthModal.vue'
import {
  clearVisitorLeadData,
  deleteVisitorScan,
  getInvalidVisitorScans,
  getVisitorLeadSetting,
  getVisitorScans,
  saveInvalidVisitorScan,
  saveVisitorScan,
  setVisitorLeadSetting
} from '@/components/visitor-lead/storage.js'
import { syncVisitorLead } from '@/components/visitor-lead/api.js'
import {
  ALLOW_VISITOR_LEAD_DEBUG,
  VISITOR_LEAD_DEBUG_PASSWORD,
  VISITOR_LEAD_DEBUG_TAP_COUNT,
  VISITOR_LEAD_DEBUG_TAP_WINDOW_MS,
  getVisitorLeadValidationError,
  parseVisitorLeadCode
} from '@/components/visitor-lead/config.js'
import { getVisitorLeadLocale, translateVisitorLead, translateVisitorLeadError } from '@/components/visitor-lead/i18n.js'
import { loadVisitorLeadFallbackModule } from '@/components/visitor-lead/fallback-loader.js'
import { slugify } from '@/components/lib/speaker-utils.js'

const PAGE_SIZE = 25
const TAB_NAMES = ['scan', 'list', 'settings']
const NO_FAIR_ID = 'OHNE_MESSE'
const LOGIN_TICKET_SETTING_KEY = 'visitorLeadSelectedTicketId'
const LOGIN_TICKET_CONTEXT_SETTING_KEY = 'visitorLeadSelectedTicketContext'
const DEBUG_ENABLED_SETTING_KEY = 'visitorLeadDebugEnabled'

function createId() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID()
  }
  return `visitor-lead-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function normalizeCode(code) {
  return String(code || '').trim().toUpperCase()
}

function extractUserTicketAuth(ticket) {
  const fallbackBarcode = normalizeCode(ticket?.ticket_id || '')
  const fairId = normalizeCode(ticket?.event_short_name || '')
  const pdfUrl = String(ticket?.pdf_url || '').trim()

  if (!pdfUrl || !fairId) {
    return { userTicketBarcode: fallbackBarcode, userTicketSecret: '' }
  }

  try {
    const url = new URL(pdfUrl, window.location.origin)
    const pathSegments = url.pathname.split('/').filter(Boolean)
    const barcodePrefix = `FV${fairId}.`
    const barcodeIndex = pathSegments.findIndex((segment) => normalizeCode(segment).startsWith(barcodePrefix))

    if (barcodeIndex < 0) {
      return { userTicketBarcode: fallbackBarcode, userTicketSecret: '' }
    }

    return {
      userTicketBarcode: normalizeCode(pathSegments[barcodeIndex] || fallbackBarcode),
      userTicketSecret: String(pathSegments[barcodeIndex + 1] || '').trim()
    }
  } catch (error) {
    return { userTicketBarcode: fallbackBarcode, userTicketSecret: '' }
  }
}

export default {
  name: 'ExhibitorScan',
  components: {
    VisitorLeadScanTab,
    VisitorLeadListTab,
    VisitorLeadSettingsTab,
    VisitorLeadModal,
    VisitorLeadFairSelectorModal,
    VisitorLeadDebugAuthModal,
  },
  data() {
    return {
      debugFeatureAvailable: ALLOW_VISITOR_LEAD_DEBUG,
      debugEnabled: false,
      aboutTapTimestamps: [],
      debugAuthVisible: false,
      debugAuthPasswordError: '',
      activeTab: 'scan',
      userProfile: {},
      availableTickets: [],
      ticketsLoading: false,
      ticketsError: '',
      activeScanTicketId: '',
      storedActiveScanTicket: null,
      ticketPickerFairId: '',
      pendingScanContext: null,
      scans: [],
      invalidScans: [],
      selectedFairId: '',
      currentPage: 1,
      fairSelectorVisible: false,
      modal: {
        visible: false,
        variant: 'info',
        title: '',
        text: '',
        primaryLabel: '',
        secondaryLabel: '',
        onPrimary: null,
        onSecondary: null
      },
      locale: getVisitorLeadLocale(),
      liveScannerKey: 0,
      lastScan: { code: '', time: 0 },
      liveScannerFailed: false,
      storageReady: true,
      fallbackAvailable: false,
      fallbackModule: null,
      fallbackAutoEnabled: false,
      fallbackForcedEnabled: false,
      capturePreviewUrl: ''
    }
  },
  computed: {
    unsyncedCount() {
      return this.scans.filter(scan => scan.status === 'pending' || scan.status === 'failed').length
    },
    availableFairs() {
      const fairMap = new Map()
      this.scans.forEach((scan) => {
        const fairId = scan.fairId || NO_FAIR_ID
        const current = fairMap.get(fairId)
        const scannedAt = new Date(scan.scannedAt).getTime()
        if (!current) {
          fairMap.set(fairId, { id: fairId, count: 1, latestScannedAt: scannedAt })
          return
        }
        current.count += 1
        current.latestScannedAt = Math.max(current.latestScannedAt, scannedAt)
      })
      return Array.from(fairMap.values()).sort((left, right) => right.latestScannedAt - left.latestScannedAt)
    },
    visibleFairTabs() {
      return this.availableFairs.slice(0, 3)
    },
    moreFairsAvailable() {
      return this.availableFairs.length > 3
    },
    filteredScans() {
      if (!this.selectedFairId) return this.scans
      return this.scans.filter(scan => (scan.fairId || NO_FAIR_ID) === this.selectedFairId)
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredScans.length / PAGE_SIZE))
    },
    pagedScans() {
      const startIndex = (this.currentPage - 1) * PAGE_SIZE
      return this.filteredScans.slice(startIndex, startIndex + PAGE_SIZE)
    },
    scanMode() {
      if (this.fallbackForcedEnabled && this.fallbackAvailable) return 'fallback'
      if (this.fallbackAutoEnabled && this.liveScannerFailed && this.fallbackAvailable) return 'fallback'
      return 'live'
    },
    showLiveScanner() {
      return this.activeTab === 'scan' && this.scanMode === 'live' && this.hasActiveScanLogin && !this.ticketPickerFairId
    },
    invalidScansExport() {
      return this.invalidScans.map((scan) => {
        const segments = [scan.code]
        if (scan.errorCode) segments.push(`[${scan.errorCode}]`)
        if (scan.message || scan.errorCode) segments.push(this.errorText(scan.errorCode, scan.message))
        return segments.join(' ')
      }).join('\n')
    },
    availableTicketFairs() {
      const fairMap = new Map()
      this.availableTickets.forEach((ticket) => {
        const fairId = ticket.fairId || NO_FAIR_ID
        const current = fairMap.get(fairId)
        const timestamp = ticket.sortTimestamp
        if (!current) {
          fairMap.set(fairId, { id: fairId, count: 1, latestTimestamp: timestamp })
          return
        }
        current.count += 1
        current.latestTimestamp = Math.max(current.latestTimestamp, timestamp)
      })
      return Array.from(fairMap.values()).sort((left, right) => right.latestTimestamp - left.latestTimestamp)
    },
    ticketPickerOptions() {
      if (!this.ticketPickerFairId) return []
      return this.availableTickets
        .filter((ticket) => (ticket.fairId || NO_FAIR_ID) === this.ticketPickerFairId)
        .sort((left, right) => right.sortTimestamp - left.sortTimestamp)
    },
    activeScanTicket() {
      return this.availableTickets.find((ticket) => String(ticket.id) === String(this.activeScanTicketId))
        || (this.storedActiveScanTicket && String(this.storedActiveScanTicket.id) === String(this.activeScanTicketId)
          ? this.storedActiveScanTicket
          : null)
    },
    hasActiveScanLogin() {
      return Boolean(this.activeScanTicket)
    },
    activeScanFairId() {
      return this.activeScanTicket ? this.activeScanTicket.fairId : ''
    },
    activeScanUserLabel() {
      const firstName = this.userProfile.first_name || ''
      const lastName = this.userProfile.last_name || ''
      const fullName = [firstName, lastName].filter(Boolean).join(' ').trim()
      return fullName || this.userProfile.username || this.t('scan.unknownUser')
    },
    activeScanContextLabel() {
      if (!this.activeScanTicket) return this.t('scan.noActiveLogin')
      return `${this.activeScanTicket.fairId} \u00b7 ${this.ticketDisplayTitle(this.activeScanTicket)}`
    },
    showScanLoginSelector() {
      return this.activeTab === 'scan' && (!this.hasActiveScanLogin || Boolean(this.ticketPickerFairId))
    },
    debugScanLoginSummary() {
      return JSON.stringify({
        user_uuid: this.$storage.getUserUuid ? this.$storage.getUserUuid() : '',
        fair_id: this.activeScanFairId,
        user_ticket_barcode: this.activeScanTicket ? this.activeScanTicket.userTicketBarcode : '',
        user_ticket_secret: this.activeScanTicket ? this.activeScanTicket.userTicketSecret : '',
        ticket_id: this.activeScanTicket ? this.activeScanTicket.id : '',
        event_name: this.activeScanTicket ? this.activeScanTicket.eventName : '',
        product_name: this.activeScanTicket ? this.activeScanTicket.productName : '',
        raw_user_profile: this.userProfile || null,
        raw_ticket: this.activeScanTicket || null,
      }, null, 2)
    }
  },
  watch: {
    activeTab(newTab) {
      this.updateRouteTab(newTab)
      if (newTab === 'scan' && this.scanMode === 'live') {
        this.liveScannerKey += 1
      }
    },
    scans: {
      deep: true,
      handler() {
        this.ensureSelectedFair()
        if (this.currentPage > this.totalPages) {
          this.currentPage = this.totalPages
        }
      }
    }
  },
  async created() {
    this.handleOnline = () => this.syncPendingScans()
    window.addEventListener('online', this.handleOnline)
    this.activeTab = this.getInitialTab()

    await this.initializeStorageState()
    await this.initializeScanLoginState()
    this.fallbackModule = await loadVisitorLeadFallbackModule()
    this.fallbackAvailable = Boolean(this.fallbackModule)

    if (navigator.onLine) {
      await this.syncPendingScans()
    }
  },
  beforeDestroy() {
    window.removeEventListener('online', this.handleOnline)
    this.clearCapturePreview()
  },
  methods: {
    t(key, replacements = {}) {
      return translateVisitorLead(key, replacements, this.locale)
    },
    errorText(code, fallbackMessage = '', replacements = {}) {
      return translateVisitorLeadError(code, fallbackMessage, this.locale, replacements)
    },
    getInitialTab() {
      const requestedTab = String(this.$route.query.tab || '').toLowerCase()
      return TAB_NAMES.includes(requestedTab) ? requestedTab : 'scan'
    },
    setActiveTab(tabName) {
      if (!TAB_NAMES.includes(tabName)) return
      this.activeTab = tabName
    },
    updateRouteTab(tabName) {
      if (this.$route.query.tab === tabName) return
      this.$router.replace({
        path: this.$route.path,
        query: { ...this.$route.query, tab: tabName }
      }).catch(() => {})
    },
    normalizeTicket(ticket) {
      const auth = extractUserTicketAuth(ticket)
      return {
        ...ticket,
        id: ticket.id,
        ticketBarcode: ticket.ticket_id || '',
        userTicketBarcode: auth.userTicketBarcode,
        userTicketSecret: auth.userTicketSecret,
        fairId: String(ticket.event_short_name || '').toUpperCase(),
        eventName: ticket.event_name || '',
        productName: ticket.product_name || '',
        timestamp: ticket.timestamp || '',
        sortTimestamp: ticket.timestamp ? new Date(ticket.timestamp).getTime() : 0
      }
    },
    async fetchCurrentUserProfile() {
      try {
        const response = await this.$storage.fetchUser()
        this.userProfile = response.data || this.$storage.user || {}
      } catch (error) {
        this.userProfile = this.$storage.user || {}
      }
    },
    async fetchCurrentTickets() {
      this.ticketsLoading = true
      this.ticketsError = ''
      try {
        const response = await this.$api.get('/tickets/', { params: { current: 1 } })
        const items = Array.isArray(response.data?.results) ? response.data.results : (Array.isArray(response.data) ? response.data : [])
        this.availableTickets = items.map(this.normalizeTicket).filter(ticket => ticket.ticketBarcode && ticket.fairId)
      } catch (error) {
        this.availableTickets = []
        this.ticketsError = error.response?.data?.detail || this.t('ticket.loadError')
      } finally {
        this.ticketsLoading = false
      }
    },
    async initializeScanLoginState() {
      await this.fetchCurrentUserProfile()
      await this.fetchCurrentTickets()
      await this.restoreActiveScanTicket()
    },
    async restoreActiveScanTicket() {
      const storedTicketId = await getVisitorLeadSetting(LOGIN_TICKET_SETTING_KEY, '')
      const storedTicketContext = await getVisitorLeadSetting(LOGIN_TICKET_CONTEXT_SETTING_KEY, null)
      const preferredFairId = String(this.$storage.observed?.currentEventShortName || '').toUpperCase()
      const restoredTicket = this.availableTickets.find((ticket) => String(ticket.id) === String(storedTicketId))

      if (restoredTicket) {
        this.storedActiveScanTicket = restoredTicket
        this.activeScanTicketId = String(restoredTicket.id)
        this.ticketPickerFairId = ''
        return
      }

      if (storedTicketContext && storedTicketContext.ticketBarcode && storedTicketContext.fairId) {
        this.storedActiveScanTicket = storedTicketContext
        this.activeScanTicketId = String(storedTicketContext.id)
        this.ticketPickerFairId = ''
        return
      }

      await this.setVisitorLeadSettingSafe(LOGIN_TICKET_SETTING_KEY, '')
      await this.setVisitorLeadSettingSafe(LOGIN_TICKET_CONTEXT_SETTING_KEY, null)
      this.activeScanTicketId = ''
      this.storedActiveScanTicket = null

      if (this.availableTicketFairs.length === 1) {
        const [singleFair] = this.availableTicketFairs
        const matchingTickets = this.availableTickets.filter((ticket) => ticket.fairId === singleFair.id)
        if (matchingTickets.length === 1) {
          await this.activateScanTicket(matchingTickets[0], { persistOnly: true })
          return
        }
        this.ticketPickerFairId = singleFair.id
        return
      }

      this.ticketPickerFairId = this.availableTicketFairs.find((fair) => fair.id === preferredFairId)?.id
        || this.availableTicketFairs[0]?.id
        || ''
    },
    async setVisitorLeadSettingSafe(key, value) {
      if (!this.storageReady) return
      await setVisitorLeadSetting(key, value)
    },
    ensureSelectedFair() {
      const availableFairIds = this.availableFairs.map(fair => fair.id)
      if (!availableFairIds.length) {
        this.selectedFairId = ''
        return
      }
      if (availableFairIds.includes(this.selectedFairId)) return
      this.selectedFairId = availableFairIds[0]
    },
    selectFair(fairId) {
      this.selectedFairId = fairId
      this.currentPage = 1
    },
    selectFairFromModal(fairId) {
      this.selectFair(fairId)
      this.fairSelectorVisible = false
    },
    openTicketPicker(fairId = '') {
      const nextFairId = fairId || this.activeScanFairId || this.availableTicketFairs[0]?.id || ''
      this.ticketPickerFairId = nextFairId
      this.liveScannerKey += 1
    },
    closeTicketPicker() {
      this.ticketPickerFairId = ''
      this.pendingScanContext = null
      if (this.activeTab === 'scan' && this.hasActiveScanLogin && this.scanMode === 'live') {
        this.liveScannerKey += 1
      }
    },
    async activateScanTicket(ticket, options = {}) {
      this.activeScanTicketId = String(ticket.id)
      this.storedActiveScanTicket = ticket
      await this.setVisitorLeadSettingSafe(LOGIN_TICKET_SETTING_KEY, this.activeScanTicketId)
      await this.setVisitorLeadSettingSafe(LOGIN_TICKET_CONTEXT_SETTING_KEY, ticket)

      if (!options.keepPickerOpen) {
        this.closeTicketPicker()
      }

      const pendingResumeCode = this.pendingScanContext && this.pendingScanContext.fairId === ticket.fairId
        ? this.pendingScanContext.resumeCode
        : ''
      const codeToResume = options.resumeCode || pendingResumeCode
      if (codeToResume) {
        this.pendingScanContext = null
        await this.createScan(codeToResume)
      }
    },
    ticketDisplayTitle(ticket) {
      return ticket.productName || this.t('ticket.fallbackLabel', { barcode: ticket.ticketBarcode })
    },
    openExhibitorProfile(scan) {
      this.openResolvedExhibitorProfile(scan)
    },
    async openResolvedExhibitorProfile(scan) {
      const resolvedScan = await this.resolveExhibitorReference(scan)
      if (!/^\d+$/.test(String(resolvedScan?.exhibitorId || ''))) return

      const slugSource = resolvedScan.companyName || this.scanDisplayName(resolvedScan) || resolvedScan.code || `exhibitor-${resolvedScan.exhibitorId}`
      this.$router.push({
        path: `/exhibitors/${resolvedScan.exhibitorId}-${slugify(slugSource)}`,
        query: { from: 'ExhibitorScanView' }
      })
    },
    isExhibitorProfileLinkable(scan) {
      return /^\d+$/.test(String(scan?.exhibitorId || '')) || Boolean(scan?.exhibitorSfid)
    },
    async resolveExhibitorReference(scan) {
      if (/^\d+$/.test(String(scan?.exhibitorId || '')) && !scan?.exhibitorSfid) return scan
      if (!scan?.exhibitorSfid) return scan

      try {
        const response = await this.$api.get('/exhibitors/', {
          params: {
            ...(scan.fairId ? { event__short_name: scan.fairId } : {}),
            sfid: scan.exhibitorSfid
          }
        })
        const results = Array.isArray(response.data?.results) ? response.data.results : []
        const exhibitor = results.find((item) => String(item.sfid || '').toUpperCase() === String(scan.exhibitorSfid).toUpperCase()) || results[0]
        if (!exhibitor) return scan

        scan.exhibitorId = exhibitor.id || scan.exhibitorId || null
        scan.companyName = exhibitor.company_name || scan.companyName || ''
        scan.logoUrl = exhibitor.logo || scan.logoUrl || ''
        scan.hall = exhibitor.hall || scan.hall || ''
        scan.stand = exhibitor.stand || scan.stand || ''
        scan.exhibitorDetailsLoaded = true

        if (this.storageReady) {
          await saveVisitorScan(scan)
        }
      } catch (error) {
        return scan
      }

      return scan
    },
    async initializeStorageState() {
      try {
        this.scans = await getVisitorScans()
        this.invalidScans = await getInvalidVisitorScans()
        this.fallbackAutoEnabled = await getVisitorLeadSetting('visitorLeadFallbackAuto', false)
        this.fallbackForcedEnabled = await getVisitorLeadSetting('visitorLeadFallbackForced', false)
        this.debugEnabled = this.debugFeatureAvailable
          ? await getVisitorLeadSetting(DEBUG_ENABLED_SETTING_KEY, false)
          : false
        this.ensureSelectedFair()
        this.storageReady = true
      } catch (error) {
        this.storageReady = false
        this.scans = []
        this.invalidScans = []
        this.fallbackAutoEnabled = false
        this.fallbackForcedEnabled = false
        this.debugEnabled = false
        this.openModal({
          title: this.t('storage.unavailableTitle'),
          text: this.t('storage.unavailableText'),
          variant: 'error',
          primaryLabel: this.t('actions.ok')
        })
      }
    },
    handleAboutTap() {
      if (!this.debugFeatureAvailable || this.debugEnabled) return

      const now = Date.now()
      this.aboutTapTimestamps = this.aboutTapTimestamps
        .filter((timestamp) => now - timestamp <= VISITOR_LEAD_DEBUG_TAP_WINDOW_MS)
      this.aboutTapTimestamps.push(now)

      if (this.aboutTapTimestamps.length >= VISITOR_LEAD_DEBUG_TAP_COUNT) {
        this.aboutTapTimestamps = []
        this.debugAuthVisible = true
        this.debugAuthPasswordError = ''
      }
    },
    closeDebugAuth() {
      this.debugAuthVisible = false
      this.debugAuthPasswordError = ''
    },
    async submitDebugPassword(password) {
      if (password !== VISITOR_LEAD_DEBUG_PASSWORD) {
        this.debugAuthPasswordError = this.t('debug.invalidPassword')
        return
      }

      this.debugAuthPasswordError = ''
      this.debugEnabled = true
      await this.setVisitorLeadSettingSafe(DEBUG_ENABLED_SETTING_KEY, true)
      this.debugAuthVisible = false
      this.openModal({
        title: this.t('debug.enabledTitle'),
        text: this.t('debug.enabledText'),
        variant: 'success',
        primaryLabel: this.t('actions.ok')
      })
    },
    async handleDecoded(decodedText) {
      const validationErrorCode = getVisitorLeadValidationError(decodedText)
      if (validationErrorCode) {
        this.openModal({
          title: this.t('scan.invalidTitle'),
          text: this.errorText(validationErrorCode),
          variant: 'error',
          primaryLabel: this.t('actions.continueScanning')
        })
        return
      }

      const parsedCode = parseVisitorLeadCode(decodedText)
      if (!parsedCode) {
        this.openModal({
          title: this.t('scan.invalidTitle'),
          text: this.t('scan.invalidUninterpretable'),
          variant: 'error',
          primaryLabel: this.t('actions.continueScanning')
        })
        return
      }

      if (!this.hasActiveScanLogin) {
        this.openTicketPicker(this.ticketPickerFairId)
        return
      }

      if (parsedCode && this.activeScanFairId && parsedCode.fairId !== this.activeScanFairId) {
        await this.handleMismatchingFairScan(parsedCode, parsedCode.payload)
        return
      }

      const now = Date.now()
      // Ignore back-to-back duplicate decode events from the scanner component.
      if (this.lastScan.code === parsedCode.payload && now - this.lastScan.time < 2000) {
        return
      }

      this.lastScan = { code: parsedCode.payload, time: now }
      await this.createScan(parsedCode)
    },
    async handleMismatchingFairScan(parsedCode, normalizedCode) {
      const matchingTickets = this.availableTickets.filter((ticket) => ticket.fairId === parsedCode.fairId)

      if (matchingTickets.length === 1) {
        this.openModal({
          title: this.t('scan.mismatchFairTitle'),
          text: this.t('scan.mismatchFairSwitchText', { fairId: parsedCode.fairId, activeFairId: this.activeScanFairId }),
          variant: 'info',
          primaryLabel: this.t('actions.switchAndCapture'),
          secondaryLabel: this.t('actions.cancel'),
          onPrimary: async () => {
            await this.activateScanTicket(matchingTickets[0], { resumeCode: normalizedCode })
          }
        })
        return
      }

      if (matchingTickets.length > 1) {
        this.openModal({
          title: this.t('scan.mismatchFairTitle'),
          text: this.t('scan.mismatchFairChooseTicketText', { fairId: parsedCode.fairId }),
          variant: 'info',
          primaryLabel: this.t('actions.chooseTicket'),
          secondaryLabel: this.t('actions.cancel'),
          onPrimary: () => {
            this.pendingScanContext = { fairId: parsedCode.fairId, resumeCode: normalizedCode }
            this.openTicketPicker(parsedCode.fairId)
          }
        })
        return
      }

      this.openModal({
        title: this.t('scan.mismatchFairTitle'),
        text: this.t('scan.mismatchFairNoTicketText', { fairId: parsedCode.fairId }),
        variant: 'error',
        primaryLabel: this.t('actions.chooseEvent'),
        secondaryLabel: this.t('actions.cancel'),
        onPrimary: () => { this.openTicketPicker() }
      })
    },
    handleScannerReady() {
      this.liveScannerFailed = false
    },
    handleScannerError(error) {
      this.liveScannerFailed = true
      if (this.fallbackAutoEnabled && this.fallbackAvailable) {
        this.openModal({
          title: this.t('scan.fallbackActivatedTitle'),
          text: this.t('scan.fallbackActivatedText'),
          variant: 'info',
          primaryLabel: this.t('actions.ok')
        })
        return
      }
      this.openModal({
        title: this.t('scan.liveUnavailableTitle'),
        text: this.errorText(error?.code, error?.message || this.t('scan.liveUnavailable')),
        variant: 'error',
        primaryLabel: this.t('actions.ok')
      })
    },
    async createScan(code) {
      const parsedCode = typeof code === 'string' ? parseVisitorLeadCode(code) : code
      const normalizedCode = parsedCode ? parsedCode.payload : normalizeCode(code)

      if (!normalizedCode) {
        this.openModal({
          title: this.t('scan.invalidTitle'),
          text: this.t('scan.invalidUnprocessable'),
          variant: 'error',
          primaryLabel: this.t('actions.ok')
        })
        return
      }

      if (!this.storageReady) {
        this.openModal({
          title: this.t('storage.unavailableTitle'),
          text: this.t('storage.unavailableScanText'),
          variant: 'error',
          primaryLabel: this.t('actions.ok')
        })
        return
      }

      if (!this.hasActiveScanLogin) {
        this.openModal({
          title: this.t('scan.noLoginTitle'),
          text: this.t('scan.noLoginText'),
          variant: 'info',
          primaryLabel: this.t('actions.chooseTicket'),
          onPrimary: () => { this.openTicketPicker(this.ticketPickerFairId) }
        })
        return
      }

      const existingScan = this.scans.find(scan => normalizeCode(scan.code) === normalizedCode)
      if (existingScan) {
        this.openModal({
          title: this.t('scan.alreadyCapturedTitle'),
          text: this.scanDisplayName(existingScan) || this.t('scan.alreadyCapturedText'),
          variant: 'info',
          primaryLabel: this.t('actions.continueScanning'),
          secondaryLabel: this.t('actions.openContact'),
          onSecondary: () => { this.setActiveTab('list') }
        })
        return
      }

      const scan = {
        id: createId(),
        code: normalizedCode,
        scannedAt: new Date().toISOString(),
        status: 'pending',
        name: '',
        firstName: '',
        lastName: '',
        companyName: '',
        jobTitle: '',
        fairId: parsedCode?.fairId || this.activeScanFairId,
        exhibitorId: null,
        exhibitorSfid: parsedCode?.codeType === 'exhibitor' ? parsedCode.identifier : '',
        userTicketBarcode: this.activeScanTicket ? this.activeScanTicket.userTicketBarcode : '',
        userTicketSecret: this.activeScanTicket ? this.activeScanTicket.userTicketSecret : '',
        userUuid: this.$storage.getUserUuid ? this.$storage.getUserUuid() : '',
        logoUrl: null,
        exhibitorDetailsLoaded: false,
        hall: null,
        stand: null
      }

      this.scans.unshift(scan)
      this.currentPage = 1
      await saveVisitorScan(scan)

      if (!navigator.onLine) {
        this.openModal({
          title: this.t('scan.offlineStoredTitle'),
          text: this.t('scan.offlineStoredText'),
          variant: 'info',
          primaryLabel: this.t('actions.continueScanning'),
          secondaryLabel: this.t('actions.openContact'),
          onSecondary: () => { this.setActiveTab('list') }
        })
        return
      }

      await this.syncSingleScan(scan, true)
    },
    async syncSingleScan(scan, showModal) {
      try {
        const data = await syncVisitorLead(this.$scanApi, scan)
        if (data.valid === false) {
          await this.handleInvalidScan(scan, data, showModal)
          return 'invalid'
        }

        scan.status = 'synced'
        scan.name = data.name || ''
        scan.firstName = data.first_name || ''
        scan.lastName = data.last_name || ''
        scan.companyName = data.company_name || ''
        scan.jobTitle = data.job_title || ''
        scan.fairId = data.fair_id || ''
        scan.exhibitorId = data.exhibitor_id || null
        scan.exhibitorSfid = data.exhibitor_sfid || scan.exhibitorSfid || ''
        await this.resolveExhibitorReference(scan)
        await this.enrichExhibitorScan(scan)
        await saveVisitorScan(scan)

        if (showModal) {
          this.openModal({
            title: this.t('scan.leadCapturedTitle'),
            text: this.scanDisplayName(scan) || scan.companyName || scan.code,
            variant: 'success',
            primaryLabel: this.t('actions.continueScanning'),
            secondaryLabel: this.t('actions.openContact'),
            onSecondary: () => { this.setActiveTab('list') }
          })
        }
        return 'synced'
      } catch (error) {
        scan.status = navigator.onLine ? 'failed' : 'pending'
        await saveVisitorScan(scan)

        if (showModal) {
          this.openModal({
            title: this.t('sync.failedTitle'),
            text: this.errorText(error?.code || error?.response?.data?.error_code, error?.message || this.t('sync.failedText')),
            variant: 'error',
            primaryLabel: this.t('actions.continueScanning'),
            secondaryLabel: this.t('actions.openContact'),
            onSecondary: () => { this.setActiveTab('list') }
          })
        }
        return 'failed'
      }
    },
    async handleInvalidScan(scan, data, showModal) {
      const invalidScan = {
        id: scan.id,
        code: scan.code,
        scannedAt: scan.scannedAt,
        message: data.message || 'No valid record was found for this QR code.',
        errorCode: data.error_code || '',
        fairId: data.fair_id || ''
      }

      this.scans = this.scans.filter(existingScan => existingScan.id !== scan.id)
      this.invalidScans.unshift(invalidScan)
      await deleteVisitorScan(scan.id)
      await saveInvalidVisitorScan(invalidScan)

      if (showModal) {
        this.openModal({
          title: this.t('sync.invalidRecordTitle'),
          text: this.errorText(invalidScan.errorCode, invalidScan.message),
          variant: 'info',
          primaryLabel: this.t('actions.continueScanning'),
          secondaryLabel: this.t('tabs.settings'),
          onSecondary: () => { this.setActiveTab('settings') }
        })
      }
    },
    async syncPendingScans() {
      // Retry pending entries automatically once the browser is online again.
      if (!navigator.onLine) return
      const pendingScans = this.scans.filter(scan => scan.status === 'pending' || scan.status === 'failed').slice()
      for (const scan of pendingScans) {
        await this.syncSingleScan(scan, false)
      }
    },
    async resyncAllScans() {
      if (!this.storageReady) {
        this.openModal({
          title: this.t('storage.unavailableTitle'),
          text: this.t('storage.unavailableResyncText'),
          variant: 'error',
          primaryLabel: this.t('actions.ok')
        })
        return
      }

      if (!navigator.onLine) {
        this.openModal({
          title: this.t('sync.offlineTitle'),
          text: this.t('sync.offlineText'),
          variant: 'error',
          primaryLabel: this.t('actions.ok')
        })
        return
      }

      if (!this.scans.length) {
        this.openModal({
          title: this.t('sync.noScansTitle'),
          text: this.t('sync.noScansText'),
          variant: 'info',
          primaryLabel: this.t('actions.ok')
        })
        return
      }

      let syncedCount = 0
      let invalidCount = 0
      let failedCount = 0

      for (const scan of this.scans.slice()) {
        try {
          const result = await this.syncSingleScan(scan, false)
          if (result === 'synced') syncedCount += 1
          else if (result === 'invalid') invalidCount += 1
          else failedCount += 1
        } catch (error) {
          failedCount += 1
        }
      }

      this.openModal({
        title: failedCount || invalidCount ? this.t('sync.completedTitle') : this.t('sync.successTitle'),
        text: failedCount
          ? this.t('sync.completedTextWithInvalid', { syncedCount, invalidCount, failedCount })
          : invalidCount
            ? this.t('sync.successTextWithInvalid', { syncedCount, invalidCount })
            : this.t('sync.successText', { syncedCount }),
        variant: failedCount || invalidCount ? 'info' : 'success',
        primaryLabel: this.t('actions.ok')
      })
    },
    changePage(direction) {
      const nextPage = this.currentPage + direction
      if (nextPage < 1 || nextPage > this.totalPages) return
      this.currentPage = nextPage
    },
    async enrichExhibitorScan(scan) {
      if (!/^\d+$/.test(String(scan.exhibitorId || ''))) return
      if (scan.exhibitorDetailsLoaded) return

      try {
        const response = await this.$api.get(`exhibitors/${scan.exhibitorId}/`)
        const exhibitor = response.data || {}
        if (exhibitor.company_name) scan.companyName = exhibitor.company_name
        if (exhibitor.logo) scan.logoUrl = exhibitor.logo
        if (exhibitor.hall) scan.hall = exhibitor.hall
        if (exhibitor.stand) scan.stand = exhibitor.stand
        scan.exhibitorDetailsLoaded = true
      } catch (error) {
        scan.exhibitorDetailsLoaded = false
      }
    },
    scanDisplayName(scan) {
      const fullName = [scan.firstName, scan.lastName].filter(Boolean).join(' ').trim()
      return fullName || scan.name || scan.companyName || ''
    },
    async copyInvalidScans() {
      if (!navigator.clipboard || !this.invalidScansExport) return
      await navigator.clipboard.writeText(this.invalidScansExport)
      this.openModal({
        title: this.t('copy.invalidScansTitle'),
        text: this.t('copy.invalidScansText'),
        variant: 'success',
        primaryLabel: this.t('actions.ok')
      })
    },
    openModal({ title, text, variant = 'info', primaryLabel = '', secondaryLabel = '', onPrimary = null, onSecondary = null }) {
      this.modal = {
        visible: true,
        title,
        text,
        variant,
        primaryLabel: primaryLabel || this.t('actions.close'),
        secondaryLabel,
        onPrimary,
        onSecondary
      }
    },
    closeModal() {
      this.modal.visible = false
    },
    runPrimaryModalAction() {
      const handler = this.modal.onPrimary
      this.closeModal()
      if (typeof handler === 'function') handler()
    },
    runSecondaryModalAction() {
      const handler = this.modal.onSecondary
      this.closeModal()
      if (typeof handler === 'function') handler()
    },
    updateFallbackAuto(val) {
      this.fallbackAutoEnabled = val
      this.persistFallbackSettings()
    },
    updateFallbackForced(val) {
      this.fallbackForcedEnabled = val
      this.persistFallbackSettings()
    },
    async persistFallbackSettings() {
      if (!this.storageReady) return
      await setVisitorLeadSetting('visitorLeadFallbackAuto', this.fallbackAutoEnabled)
      await setVisitorLeadSetting('visitorLeadFallbackForced', this.fallbackForcedEnabled)
      if (this.activeTab === 'scan') {
        this.liveScannerKey += 1
      }
    },
    async handleCapturedFile(event) {
      const file = event.target.files && event.target.files[0]
      if (!file || !this.fallbackModule) return

      this.setCapturePreview(file)

      try {
        const decodedText = await this.fallbackModule.decodeFile(file)
        await this.handleDecoded(decodedText)
      } catch (error) {
        this.openModal({
          title: this.t('scan.qrNotDetectedTitle'),
          text: this.errorText(error?.code, error?.message || this.t('error.fallback_decode_failed')),
          variant: 'error',
          primaryLabel: this.t('actions.tryAgain')
        })
      }
    },
    setCapturePreview(file) {
      this.clearCapturePreview()
      this.capturePreviewUrl = URL.createObjectURL(file)
    },
    clearCapturePreview() {
      if (this.capturePreviewUrl) {
        URL.revokeObjectURL(this.capturePreviewUrl)
        this.capturePreviewUrl = ''
      }
    },
    async resetLocalData() {
      if (!window.confirm(this.t('reset.confirm'))) return
      try {
        await clearVisitorLeadData()
      } catch (err) {
        this.openModal({ title: this.t('reset.errorTitle'), text: String(err) })
        return
      }
      this.scans = []
      this.invalidScans = []
      this.activeScanTicketId = ''
      this.storedActiveScanTicket = null
      this.ticketPickerFairId = this.availableTicketFairs[0]?.id || ''
      this.pendingScanContext = null
      this.selectedFairId = ''
      this.currentPage = 1
      this.lastScan = { code: '', time: 0 }
      this.fallbackAutoEnabled = false
      this.fallbackForcedEnabled = false
      this.debugEnabled = false
      this.aboutTapTimestamps = []
      this.closeDebugAuth()
      this.openModal({
        title: this.t('reset.successTitle'),
        text: this.t('reset.successText'),
        variant: 'success',
        primaryLabel: this.t('actions.ok')
      })
    }
  }
}
</script>

<template>
  <div>
    <!-- Bootstrap nav-tabs: Scan, List, Settings -->
    <ul class="nav nav-tabs bg-white border-bottom px-3">
      <li class="nav-item">
        <a
          class="nav-link"
          :class="{ active: activeTab === 'scan' }"
          href="#"
          @click.prevent="setActiveTab('scan')"
        >{{ t('tabs.scan') }}</a>
      </li>
      <li class="nav-item">
        <a
          class="nav-link"
          :class="{ active: activeTab === 'list' }"
          href="#"
          @click.prevent="setActiveTab('list')"
        >{{ t('tabs.list') }}</a>
      </li>
      <li class="nav-item">
        <a
          class="nav-link"
          :class="{ active: activeTab === 'settings' }"
          href="#"
          @click.prevent="setActiveTab('settings')"
        >{{ t('tabs.settings') }}</a>
      </li>
    </ul>

    <!-- Tab content -->
    <visitor-lead-scan-tab
      v-if="activeTab === 'scan'"
      :has-active-scan-login="hasActiveScanLogin"
      :active-scan-user-label="activeScanUserLabel"
      :active-scan-context-label="activeScanContextLabel"
      :active-scan-fair-id="activeScanFairId"
      :show-scan-login-selector="showScanLoginSelector"
      :available-ticket-fairs="availableTicketFairs"
      :ticket-picker-fair-id="ticketPickerFairId"
      :ticket-picker-options="ticketPickerOptions"
      :tickets-loading="ticketsLoading"
      :tickets-error="ticketsError"
      :scan-mode="scanMode"
      :show-live-scanner="showLiveScanner"
      :live-scanner-key="liveScannerKey"
      :capture-preview-url="capturePreviewUrl"
      :live-scanner-failed="liveScannerFailed"
      :locale="locale"
      @decode="handleDecoded"
      @scanner-ready="handleScannerReady"
      @scanner-error="handleScannerError"
      @capture-file="handleCapturedFile"
      @open-ticket-picker="openTicketPicker"
      @activate-ticket="activateScanTicket"
      @close-ticket-picker="closeTicketPicker"
    />

    <visitor-lead-list-tab
      v-else-if="activeTab === 'list'"
      :scans="pagedScans"
      :total-count="scans.length"
      :no-results="filteredScans.length === 0"
      :unsynced-count="unsyncedCount"
      :available-fairs="availableFairs"
      :visible-fair-tabs="visibleFairTabs"
      :more-fairs-available="moreFairsAvailable"
      :selected-fair-id="selectedFairId"
      :current-page="currentPage"
      :total-pages="totalPages"
      :debug-enabled="debugEnabled"
      :locale="locale"
      @select-fair="selectFair"
      @open-fair-modal="fairSelectorVisible = true"
      @change-page="changePage"
      @open-exhibitor-profile="openExhibitorProfile"
      @sync-pending="syncPendingScans"
    />

    <visitor-lead-settings-tab
      v-else
      :unsynced-count="unsyncedCount"
      :storage-ready="storageReady"
      :has-scan="scans.length > 0"
      :fallback-available="fallbackAvailable"
      :fallback-auto-enabled="fallbackAutoEnabled"
      :fallback-forced-enabled="fallbackForcedEnabled"
      :invalid-scans="invalidScans"
      :debug-enabled="debugEnabled"
      :debug-scan-login-summary="debugScanLoginSummary"
      :locale="locale"
      @resync-all="resyncAllScans"
      @reset-data="resetLocalData"
      @update-fallback-auto="updateFallbackAuto"
      @update-fallback-forced="updateFallbackForced"
      @copy-invalid-scans="copyInvalidScans"
      @about-tap="handleAboutTap"
    />

    <!-- Modals -->
    <visitor-lead-modal
      :modal="modal"
      @primary="runPrimaryModalAction"
      @secondary="runSecondaryModalAction"
    />

    <visitor-lead-fair-selector-modal
      :visible="fairSelectorVisible"
      :fairs="availableFairs"
      :selected-fair-id="selectedFairId"
      :locale="locale"
      @select="selectFairFromModal"
      @close="fairSelectorVisible = false"
    />

    <visitor-lead-debug-auth-modal
      :visible="debugAuthVisible"
      :auth-error="debugAuthPasswordError"
      :locale="locale"
      @submit="submitDebugPassword"
      @close="closeDebugAuth"
    />

    <!-- Hidden mount target for the optional image-decoding fallback module. -->
    <div id="visitor-lead-fallback-reader" style="display:none" aria-hidden="true"></div>
  </div>
</template>
